import type { CapabilityInstance } from 'homey-api';
import { useState, useEffect, useRef } from 'react';

import { ToastManager } from '../../ToastManager.jsx';

import { useEventCallback } from '../../hooks/useEventCallback.ts';
import { useShallowMemo } from '../../hooks/useShallowMemo.ts';

import { useDevice } from './useDevices.ts';

export function useCapabilityValues({
  deviceId,
  capabilityIds,
}: {
  deviceId: string;
  capabilityIds: string[];
}) {
  const pendingCallsRef = useRef<Record<string, Promise<void> | undefined>>({});
  const capabilityInstancesRef = useRef<Record<string, CapabilityInstance | null>>({});
  const memoizedCapabilityIds = useShallowMemo(() => {
    return capabilityIds;
  }, capabilityIds);

  const { deviceInstance, device } = useDevice({ deviceId: deviceId });

  const getInitialValues = () => {
    const initialValues: Record<string, string | number | boolean | null> = {};

    for (const capabilityId of memoizedCapabilityIds) {
      if (device?.capabilitiesObj?.[capabilityId] != null) {
        initialValues[capabilityId] = device?.capabilitiesObj?.[capabilityId]?.value ?? null;
      }
    }

    return initialValues;
  };

  const [values, setValues] = useState(() => {
    return getInitialValues();
  });
  const getInitialValuesEffectEvent = useEventCallback(getInitialValues);

  // When the device instance changes reset the values to the initial values.
  useEffect(() => {
    setValues(getInitialValuesEffectEvent());
  }, [deviceInstance, getInitialValuesEffectEvent]);

  useEffect(() => {
    const capabilityInstances = capabilityInstancesRef.current;

    for (const capabilityId of memoizedCapabilityIds) {
      if (deviceInstance?.capabilitiesObj?.[capabilityId] == null) {
        continue;
      }

      // TODO
      // connect the item so we have a list
      capabilityInstances[capabilityId] = deviceInstance.makeCapabilityInstance(
        capabilityId,
        (value) => {
          setValues((prevValues) => {
            return {
              ...prevValues,
              [capabilityId]: value,
            };
          });
        },
      );
    }

    return function () {
      for (const capabilityId of Object.keys(capabilityInstances)) {
        capabilityInstances[capabilityId]?.destroy();
        delete capabilityInstances[capabilityId];
      }
    };
  }, [memoizedCapabilityIds, deviceInstance]);

  type SetValueOptions = {
    local?: boolean;
  };

  const _setValues = useEventCallback(
    (nextValues: Record<string, null | number | string | boolean>, options?: SetValueOptions) => {
      // const isGetable = capabilityObj.getable === true;

      // if (capabilityObj.setable === false) {
      //   return Promise.reject(new Error(`Capability ${capabilityObj.id} is not setable`));
      // }

      // const prevValue = value;

      const values: Record<string, null | number | string | boolean> = {};

      for (const [capabilityId, nextValue] of Object.entries(nextValues)) {
        if (device?.capabilitiesObj?.[capabilityId] == null) {
          continue;
        }

        if (device.capabilitiesObj[capabilityId].getable === true) {
          values[capabilityId] = nextValue;
        }
      }

      setValues((prevValues) => {
        return {
          ...prevValues,
          ...values,
        };
      });

      if (options?.local === true) {
        return Promise.resolve();
      }

      for (const [capabilityId, nextValue] of Object.entries(nextValues)) {
        const capabilityInstance = capabilityInstancesRef.current[capabilityId];

        if (capabilityInstance == null) {
          continue;
        }

        const promise: Promise<void> = capabilityInstance.setValue(nextValue);
        pendingCallsRef.current[capabilityId] = promise;

        promise
          .then((result) => {
            // console.log('result', result);
          })
          .catch((error) => {
            ToastManager.handleError(error);
            // if (pendingCallsRef.current[capabilityId] === promise && isGetable === true) {
            //   setValue(prevValue);
            // }
          })
          .finally(() => {
            delete pendingCallsRef.current[capabilityId];
          });
      }

      const allPromises = Promise.all(Object.values(pendingCallsRef.current));
      allPromises.catch(() => {});

      return allPromises;
    },
  );

  return [values, _setValues] as const;
}
